<template>
    <div class="m-cart__order-list">
        <h4
            v-if="title"
            class="m-cart__order-list__header-title"
        >
            {{ title }}
        </h4>
        <div class="m-cart__order-list__ware-list">
            <div
                v-for="ware in myCart"
                :key="ware.ware_id"
                class="ware"
            >
                <div class="image">
                    <img
                        v-if="ware.post_image && ware.post_image.link"
                        :src="get(ware.post_image, 'versions.medium', ware.post_image.link)"
                        :alt="ware.ware_title"
                    >
                    <img
                        v-else
                        src="@/modules/cart/assets/images/none.jpg"
                        :alt="ware.ware_title"
                    >
                </div>
                <div class="ware-content">
                    <div class="right">
                        <span
                            v-if="ware.ware_title"
                            class="title"
                        >
                            {{ ware.ware_title.split(/ـ|-/)[0].trim() }}
                        </span>
                        <div
                            v-if="ware.post_tagging"
                            class="categories d-flex justify-content-start"
                        >
                            <span class="typo-body-2">
                                {{ `${$t('cart.order_list.categories')}: ` }}
                            </span>
                            <template v-for="cat in ware.post_tagging">
                                <router-link
                                    v-if="cat.title"
                                    :key="cat.id"
                                    :to="routeTo('products-category', cat)"
                                    class="typo-body-2 category"
                                >
                                    {{ cat.title }}
                                </router-link>
                            </template>
                        </div>
                        <span
                            v-if="ware.ware_title"
                            class="weight-title"
                        >
                            {{ `${$t('cart.order_list.weight')}: ${ware.ware_title.split(/ـ|-/)[1].trim()}` }}
                        </span>
                        <div class="counter">
                            <y-button
                                icon="plus"
                                :disabled="$wait.is('loading-change-count-item')"
                                size="xsmall"
                                color="white"
                                flat
                                @click.prevent="increase(ware)"
                            />
                            <span class="counter__number">
                                {{ $n(ware.count) }}
                            </span>
                            <y-button
                                icon="minus"
                                :disabled="$wait.is('loading-change-count-item') || ware.count < 2"
                                size="xsmall"
                                color="white"
                                flat
                                @click.prevent="decrease(ware)"
                            />
                        </div>
                    </div>
                    <div class="left">
                        <div
                            v-if="hasDiscount(ware)"
                            class="original-price"
                        >
                            <y-badge
                                :content="`${$n(discountPercent(ware))}${$t('productCard.percent_mark')}`"
                                color="primary"
                            />
                            <span class="price">
                                {{ `${$n(originalPrice(ware))} ${$t('cart.price_unit')}` }}
                            </span>
                        </div>
                        <div
                            v-if="wareSalePrice(ware)"
                            class="sale-price"
                        >
                            <span class="price">
                                {{ separated(wareSalePrice(ware), $i18n.locale) }}
                            </span>
                            <span class="price-unit">
                                {{ $t('cart.price_unit') }}
                            </span>
                        </div>
                    </div>
                    <y-button
                        class="remove"
                        icon-prepend="trash"
                        :size="onSize('remove_button', 'small')"
                        flat
                        outline
                        color="danger"
                        @click="openModal(ware)"
                    >
                        <template v-if="onSize('remove_content', true)">
                            {{ $t('cart.order_list.delete') }}
                        </template>
                    </y-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import debounce from '@nodes/helpers/api';
    import { separated } from '@nodes/helpers/number';

    import { YBadge } from '@components';

    export default {
        name: 'OrderList',

        components: {
            YBadge,
        },

        props: {
            /**
             * Title
             */
            title: {
                type: String,
            },

            /**
             * Wares List
             */
            myCart: {
                type: Array,
            },
        },

        /**
         * @inheritDoc
         */
        data() {
            return {
                newCart   : [],
                responsive: {
                    remove_content: {
                        768: false,
                    },
                    remove_button: {
                        768: 'xsmall',
                    },
                },
            };
        },

        watch: {
            newCart: {
                /**
                 * Handle watch value
                 *
                 * @param {*} newVal
                 */
                handler(newVal) {
                    if (newVal && newVal.length) {
                        this.debounceUpdateOrderList(newVal);
                    }
                },
                deep     : true,
                immediate: true,
            },
        },

        /**
         * @inheritDoc
         */
        created() {
            this.debounceUpdateOrderList = debounce((newVal) => {
                this.$emit('updateOrderList', newVal);
            }, 500);
        },

        methods: {
            separated,

            /**
             * Calculate original ware price
             * 
             * @param {object} ware
             * @returns {number}
             */
            originalPrice(ware) {
                let price = ware.single_price;
                if (ware.single_tax) {
                    price += ware.single_tax;
                }

                return price * ware.count;
            },

            /**
             * Calculate ware price
             * 
             * @param {object} ware
             * @returns {number}
             */
            wareSalePrice(ware) {
                let price = ware.single_price;
                if (ware.single_tax) {
                    price += ware.single_tax;
                }
                if (ware.single_discount) {
                    price -= ware.single_discount;
                }

                return price * ware.count;
            },

            /**
             * Increase ware count
             * 
             * @param {object} ware
             */
            increase(ware) {
                const findWare = this.myCart.find((item) => item.ware_id === ware.ware_id);
                if (findWare) {
                    findWare.count += 1;
                }
                this.newCart = this.myCart;
                this.newCart[this.newCart.findIndex((item) => item.ware_id === ware.ware_id)] = findWare;
            },

            /**
             * Decrease ware count
             * 
             * @param {object} ware
             */
            decrease(ware) {
                const findWare = this.myCart.find((item) => item.ware_id === ware.ware_id);
                if (findWare) {
                    findWare.count = findWare.count <= 0 ? 0 : findWare.count -= 1;
                }
                this.newCart = this.myCart;
                this.newCart[this.newCart.findIndex((item) => item.ware_id === ware.ware_id)] = findWare;
            },

            /**
             * Open modal
             *
             * @param {object} item - select item
             */
            openModal(item) {
                this.$emit('openModal', item);
            },

            /**
             * Calculator Percent Discount
             *
             * @param {object} ware
             * @returns {number | string}
             */
            discountPercent(ware) {
                const salePrice = ware.single_price - ware.single_discount;
                const val = (
                    (
                        (ware.single_price - salePrice)
                        / ware.single_price
                    )
                    * 100);
                // eslint-disable-next-line no-nested-ternary
                return val.toFixed(1)?.toString()?.split('.')[1] === '0'
                    ? Math.round(val) : Math.round(val) > 1
                        ? Math.round(val) : val.toFixed(1);
            },

            /**
             * Check if product has discount
             *
             * @param {object} ware
             * @returns {boolean}
             */
            hasDiscount(ware) {
                const salePrice = ware.single_price - ware.single_discount;
                return ware.single_price !== salePrice;
            },
        },
    };
</script>
